/** Cold-search row helpers shared by the cold-search screen and the candidate
 * card — both render the same per-row actions. */
import type { ColdSearchResult, PipelineStage } from "@/types/domain";

const SOURCE_LABEL: Record<string, string> = {
  work_ua: "Work.ua",
  robota_ua: "Robota.ua",
  internal: "Внутрішня база",
  // A PeopleForce-born candidate has no job-board résumé behind them, so
  // «Внутрішня база» is the honest origin label rather than a raw enum value.
  peopleforce: "Внутрішня база",
};

export function sourceLabel(source: string | null | undefined): string {
  if (!source) return "Внутрішня база";
  return SOURCE_LABEL[source] ?? source;
}

/** True for an origin with no job-board résumé page behind it. */
export function isInternalOrigin(source: string | null | undefined): boolean {
  return !source || source === "internal" || source === "peopleforce";
}

/** Where the résumé actually LIVES: the row's own source for a fresh external
 * hit, else the candidate's origin.
 *
 * ``result.source`` answers a different question — which CHANNEL surfaced the
 * row this run. Once a run ingests a Work.ua/Robota.ua lead into our DB, every
 * later run re-finds that person through the internal vector matcher, so the
 * row says «internal» while the résumé is still on Robota.ua. Labelling the
 * card with the channel is what put «Внутрішня база» next to «Відкрити резюме
 * на Robota.ua» on the same card. The channel is not lost — see
 * :func:`foundInOurDatabase`. */
export function originSource(result: ColdSearchResult): string {
  return result.source && !isInternalOrigin(result.source)
    ? result.source
    : result.candidate.source;
}

/** The row was surfaced by the internal matcher, not by a fresh query to the
 * job board — worth saying when the label now names that board. */
export function foundInOurDatabase(result: ColdSearchResult): boolean {
  return isInternalOrigin(result.source);
}

/** «Не цікавий» уже зафіксовано. ``dismissed_at`` теж перевіряємо: в
 * накопиченому списку представник рядка обирається за оцінкою, тож
 * ``status`` може прийти з іншого прогону, ніж позначка про відмову. */
export function isDismissed(result: ColdSearchResult): boolean {
  return result.status === "dismissed" || result.dismissed_at !== null;
}

/** Stages sorted by ``position`` (ascending), so the «Цікавий» dropdown
 * lists them in the recruiter's pipeline order. */
export function sortStages(stages: PipelineStage[]): PipelineStage[] {
  return [...stages].sort((a, b) => a.position - b.position);
}

/** The default placement stage: PeopleForce's ``position``-1 stage
 * (e.g. «Новий кандидат»), or the lowest-position stage if none has
 * ``position === 1``. ``null`` when there are no stages. */
export function defaultStage(stages: PipelineStage[]): PipelineStage | null {
  if (stages.length === 0) return null;
  const sorted = sortStages(stages);
  return sorted.find((s) => s.position === 1) ?? sorted[0] ?? null;
}
